import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { FileUploadService } from '../api-services/file-upload.service';
import { CustomFieldTest } from '../data-models/custom-field-test.model';
import { CustomFieldTestType } from '../data-models/custom-field-test-type.model';
import { CustomFieldTestRequest } from '../request-models/custom-field-test-request.model';
import { CustomFieldTestFileRequest } from '../request-models/custom-field-test-file-request.model';
import { FieldTestUploadUrl } from '../response-models/field-test-upload-url.model';
import { CUSTOM_FIELD_TEST_TYPE_OPTIONS } from '../../shared/constants/field-test-options';
import { toCustomFieldTestFileRequest, toCustomFieldTestRequest } from '../utils/field-test-utils';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

const CATEGORY_LABELS: Record<string, string> = {
  'pre-season': 'Pre-Season',
  'in-season': 'In-Season',
  'post-season': 'Post-Season',
  'maintenance': 'Maintenance'
};

const FREQUENCY_LABELS: Record<string, string> = {
  'as-needed': 'As Needed',
  'every-flight': 'Every Flight',
  'daily': 'Daily',
  'weekly': 'Weekly',
  'monthly': 'Monthly',
  'annually': 'Annually'
};

/**
 * Custom field test submissions (uploaded test documents): `/field-test-submissions[/:id[/files[/:fileId]|
 * /increment-usage]]`, `/upload-url` and `/download-url` (hub `user-field-test-submissions-controller.ts`).
 * Paged with `page` / `limit`. Files are private: view them through `getDownloadUrl()`.
 *
 * Holds the last loaded page as signal state. Failures are recorded in `error` (the hub's message) and the
 * Promise helpers resolve `null` / `false`.
 */
@Injectable({
  providedIn: 'root'
})
export class FieldTestSubmissionService extends BaseService<CustomFieldTest> {
  private readonly fileUploads = inject(FileUploadService);
  private customTestsSignal = signal<CustomFieldTest[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  readonly customTests = computed(() => this.customTestsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  readonly customTestTypeOptions = CUSTOM_FIELD_TEST_TYPE_OPTIONS;

  constructor() {
    super('field-test-submissions', CustomFieldTest);
  }

  /** Load a page of submissions into `customTests`. Failures are logged and recorded in `error`. */
  async loadCustomTests(page: number = 1, limit: number = 100): Promise<void> {
    try {
      const result = await firstValueFrom(this.getPage({ query: { page, limit } }));
      this.customTestsSignal.set(result.items);
    } catch (error: unknown) {
      this.recordError(error, 'Failed to load custom tests');
    }
  }

  getCustomTests(): CustomFieldTest[] {
    return this.customTestsSignal();
  }

  getCustomTestById(id: string): CustomFieldTest | undefined {
    return this.customTestsSignal().find(t => t.id === id);
  }

  /** Fetch one submission with its files; resolves `null` on failure. */
  async fetchCustomTestById(id: string): Promise<CustomFieldTest | null> {
    try {
      return await firstValueFrom(this.get(id));
    } catch (error: unknown) {
      console.error('Error fetching custom test:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** A pre-signed upload URL and the path to store for a new file; resolves `null` on failure. */
  async getUploadUrl(fileName: string, fileType: string): Promise<FieldTestUploadUrl | null> {
    try {
      return await firstValueFrom(this.request('POST', { path: 'upload-url' }, toUploadUrl, { fileName, fileType }));
    } catch (error: unknown) {
      console.error('Error getting upload URL:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /**
   * PUT a file to S3 with a pre-signed URL. Uses `fetch`, not HttpClient, so the auth interceptor never sends
   * the Firebase token to S3. Resolves `false` on failure.
   */
  async uploadFile(uploadUrl: string, file: File): Promise<boolean> {
    try {
      await this.fileUploads.uploadToSignedUrl(uploadUrl, file);
      return true;
    } catch (error: unknown) {
      console.error('Error uploading file:', error);
      return false;
    }
  }

  /** A temporary signed URL for a private file path; resolves `null` on failure. */
  async getDownloadUrl(filePath: string): Promise<string | null> {
    try {
      return await firstValueFrom(this.request('POST', { path: 'download-url' }, toDownloadUrl, { filePath }));
    } catch (error: unknown) {
      console.error('Error getting download URL:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** Create a submission (with its files) and prepend it to `customTests`; resolves `null` on failure. */
  async createCustomTest(dto: CustomFieldTestRequest): Promise<CustomFieldTest | null> {
    return this.track('Failed to create custom test', async () => {
      const created = await firstValueFrom(this.create(toCustomFieldTestRequest(dto)));
      if (created) {
        this.customTestsSignal.update(tests => [created, ...tests]);
      }
      return created;
    });
  }

  /** Update a submission's metadata (files are added and removed separately); resolves `null` on failure. */
  async updateCustomTest(id: string, dto: CustomFieldTestRequest): Promise<CustomFieldTest | null> {
    return this.track('Failed to update custom test', async () => {
      const updated = await firstValueFrom(this.update(id, toCustomFieldTestRequest({ ...dto, files: undefined })));
      if (updated) {
        this.customTestsSignal.update(tests => tests.map(t => (t.id === id ? updated : t)));
      }
      return updated;
    });
  }

  /** Delete a submission and remove it from `customTests`; resolves `false` on failure. */
  async deleteCustomTest(id: string): Promise<boolean> {
    const deleted = await this.track('Failed to delete custom test', async () => {
      await firstValueFrom(this.delete(id));
      this.customTestsSignal.update(tests => tests.filter(t => t.id !== id));
      return true;
    });
    return deleted ?? false;
  }

  /** Attach an uploaded file to a submission; resolves `false` on failure. */
  async addFileToCustomTest(submissionId: string, file: CustomFieldTestFileRequest): Promise<boolean> {
    return this.succeeds('Error adding file to custom test', this.request('POST', { id: submissionId, path: 'files' }, () => true, toCustomFieldTestFileRequest(file)));
  }

  /** Remove a file from a submission; resolves `false` on failure. */
  async removeFileFromCustomTest(submissionId: string, fileId: string): Promise<boolean> {
    return this.succeeds('Error removing file from custom test', this.request('DELETE', { id: submissionId, path: ['files', fileId] }, () => true));
  }

  /** Count one more use of a submission. Failures are logged only. */
  async incrementUsage(id: string): Promise<void> {
    await this.succeeds('Error incrementing usage', this.request('POST', { id, path: 'increment-usage' }, () => true, {}));
  }

  getCustomTestTypeIcon(type: CustomFieldTestType | null | undefined): string {
    switch (type) {
      case 'nozzle-calibration': return 'bi-droplet-half';
      case 'equipment-inspection': return 'bi-clipboard-check';
      case 'spray-pattern': return 'bi-grid-3x3';
      case 'flow-rate': return 'bi-speedometer2';
      case 'tank-calibration': return 'bi-bucket';
      default: return 'bi-file-earmark';
    }
  }

  getCustomTestTypeLabel(type: CustomFieldTestType | null | undefined): string {
    return this.customTestTypeOptions.find(t => t.value === type)?.label || type || '';
  }

  getCategoryLabel(category: string | null | undefined): string {
    return (category && CATEGORY_LABELS[category]) || category || '';
  }

  getFrequencyLabel(frequency: string | null | undefined): string {
    return (frequency && FREQUENCY_LABELS[frequency]) || frequency || '';
  }

  getFileIcon(type: string): string {
    if (type === 'application/pdf' || type === 'pdf') return 'bi-file-earmark-pdf';
    if (type.startsWith('image/') || ['jpg', 'jpeg', 'png'].includes(type)) return 'bi-file-earmark-image';
    if (type.includes('word') || ['doc', 'docx'].includes(type)) return 'bi-file-earmark-word';
    return 'bi-file-earmark';
  }

  /** Runs a write with the loading flag set; a failure is recorded in `error` and resolves `null`. */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action();
    } catch (error: unknown) {
      this.recordError(error, fallback);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /** Resolves `true` when the request succeeds, otherwise logs and resolves `false`. */
  private async succeeds(label: string, request: Observable<boolean>): Promise<boolean> {
    try {
      return await firstValueFrom(request);
    } catch (error: unknown) {
      console.error(`${label}:`, ApiError.fromHttpError(error).message);
      return false;
    }
  }

  private recordError(error: unknown, fallback: string): void {
    const apiError = ApiError.fromHttpError(error);
    console.error(`[FieldTestSubmissionService] ${fallback}:`, apiError.message);
    this.errorSignal.set(apiError.errors[0] ?? fallback);
  }
}

function toUploadUrl(data: unknown): FieldTestUploadUrl | null {
  const source = isRecord(data) ? data : {};
  const uploadUrl = nullableString(source['uploadUrl']);
  const filePath = nullableString(source['filePath']);
  return uploadUrl && filePath ? { uploadUrl, filePath } : null;
}

function toDownloadUrl(data: unknown): string | null {
  return (isRecord(data) ? nullableString(data['downloadUrl']) : undefined) ?? null;
}
